import React, { useRef } from 'react';
import { IconSparkles } from '@tabler/icons-react';
import { Anchor, Glow, Pill, Core, Wires, Sparks, gsap, travel, flare, blink, charge, ambience, entrance, useSceneAnimation } from '../Scene';
import './styles.scss';

// Board: the core charges the credits card above it (bars fill with light, the
// counter climbs to the plan's monthly credits), then sends light down to each
// benefit it unlocks. The CSS default is the composed final frame.
const BARS = [34, 46, 40, 58, 52, 66, 48, 74, 62, 82, 70, 88, 78, 94];
const OUTPUTS = [
  { x: 74, y: 330, wire: 'M144 262 H86 Q74 262 74 274 V317', joints: [[144, 262], [74, 317]] },
  { x: 180, y: 368, wire: 'M180 298 V355', joints: [[180, 298], [180, 355]] },
  { x: 286, y: 330, wire: 'M216 262 H274 Q286 262 286 274 V317', joints: [[216, 262], [286, 317]] },
];
const SPARKS = [[132, 236], [228, 232], [150, 300], [214, 306], [96, 112], [270, 96]];
const STEP = 1.15;

const makeBuild = ({ units, locale, outputs }) => (q, { still }) => {
  const bars = q('[data-bar]');
  const counter = q('[data-counter]')[0];
  const format = value => new Intl.NumberFormat(locale).format(Math.round(value));
  if (still) {
    gsap.set(q(`[data-ring="${outputs[1]?.id}"]`), { opacity: 0.9 });
    return;
  }
  ambience(q);
  const intro = entrance(q);
  intro.from(bars, { scaleY: 0, transformOrigin: '50% 100%', duration: 0.6, ease: 'power3.out', stagger: 0.035 }, 0.3);
  const reset = () => {
    gsap.set(bars, { opacity: 0.42 });
    if (counter) counter.textContent = format(0);
  };
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 1, delay: intro.duration() - 0.2, onStart: reset, onRepeat: reset });
  charge(loop, q, 0);
  blink(loop, q, ['charge-0'], 0.05);
  travel(loop, q, 'charge', 0.1, 0.6);
  blink(loop, q, ['charge-1'], 0.68);
  const tally = { value: 0 };
  loop.to(bars, { opacity: 1, duration: 0.24, ease: 'power2.out', stagger: 0.05 }, 0.7)
    .fromTo(tally, { value: 0 }, { value: units, duration: 1.1, ease: 'power2.out', onUpdate: () => { if (counter) counter.textContent = format(tally.value); } }, 0.7);
  outputs.forEach(({ id }, i) => {
    const at = 2.1 + i * STEP;
    charge(loop, q, at);
    travel(loop, q, id, at + 0.05, 0.6);
    blink(loop, q, [`${id}-1`], at + 0.62);
    flare(loop, q, id, at + 0.64, 0.45);
  });
  loop.to(bars, { opacity: 0.42, duration: 0.6, ease: 'power2.inOut' }, `+=${0.6}`);
};

// `outputs`: up to three `{ id, icon, label }` (labels ≤ 16 characters).
export default function PlanScene({ units, locale, outputs, unitsLabel }) {
  const stage = useRef(null);
  const placed = outputs.slice(0, 3).map((output, i) => ({ ...output, ...OUTPUTS[i] }));
  const wires = [
    { id: 'charge', tone: 'violet', d: 'M180 226 V196', joints: [[180, 226], [180, 196]] },
    ...placed.map((output, i) => ({ id: output.id, tone: i === 1 ? 'warm' : 'cool', d: output.wire, joints: output.joints })),
  ];
  useSceneAnimation(stage, makeBuild({ units, locale, outputs: placed }));
  return <div className="studio-feature-art studio-plan-art" aria-hidden="true">
    <div className="studio-feature-art__viewport">
      <div className="studio-feature-art__stage" ref={stage}>
        <Glow x={110} y={300} tone="warm" />
        <Glow x={250} y={120} tone="cool" />
        <Glow x={170} y={150} tone="violet" size={220} />
        <Wires wires={wires} />
        <Sparks points={SPARKS} />
        <Anchor x={180} y={124}>
          <div className="studio-feature-art__card studio-plan-art__meter" data-enter>
            <div className="studio-plan-art__meter-head">
              <span className="studio-plan-art__meter-label">{unitsLabel}</span>
              <IconSparkles aria-hidden="true" />
            </div>
            <b className="studio-plan-art__meter-value" data-counter>{new Intl.NumberFormat(locale).format(units)}</b>
            <div className="studio-plan-art__bars">
              {BARS.map((height, i) => <i key={i} data-bar={i} style={{ '--h': `${height}%`, '--t': i / (BARS.length - 1) }} />)}
            </div>
          </div>
        </Anchor>
        {placed.map((output, i) => <Anchor key={output.id} x={output.x} y={output.y}>
          <Pill id={output.id} tone={i === 1 ? 'warm' : 'cool'} icon={output.icon}>{output.label}</Pill>
        </Anchor>)}
        <Anchor x={180} y={262}><Core icon={IconSparkles} /></Anchor>
      </div>
    </div>
  </div>;
}
